import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Plus, Trash2, Pencil, Search, X, Building2, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KIND_META = {
  cliente: { label: "Cliente", tone: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", icon: User },
  fornecedor: { label: "Fornecedor", tone: "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30", icon: Building2 },
  terceiro: { label: "Terceiro", tone: "bg-purple-500/10 text-purple-300 border-purple-500/30", icon: User },
};

export const ContactsView = () => {
  const [contacts, setContacts] = useState([]);
  const [kindFilter, setKindFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = kindFilter && kindFilter !== "all" ? `?kind=${kindFilter}` : "";
      const res = await axios.get(`${API}/contacts${params}`);
      setContacts(res.data);
    } catch (e) {
      toast.error("Erro ao carregar contatos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindFilter]);

  const handleSave = async (payload) => {
    try {
      if (editing?.id) {
        await axios.put(`${API}/contacts/${editing.id}`, payload);
        toast.success("Contato atualizado.");
      } else {
        await axios.post(`${API}/contacts`, payload);
        toast.success("Contato criado.");
      }
      setModalOpen(false);
      setEditing(null);
      fetchContacts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar contato.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este contato?")) return;
    try {
      await axios.delete(`${API}/contacts/${id}`);
      toast.success("Contato excluído.");
      fetchContacts();
    } catch (e) {
      toast.error("Erro ao excluir.");
    }
  };

  const filtered = contacts.filter((c) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      c.name?.toLowerCase().includes(s) ||
      c.document?.toLowerCase().includes(s) ||
      c.email?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-6" data-testid="contacts-view">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#D4AF37] font-bold mb-1">
            <Users className="w-4 h-4" />
            <span>Tesouraria • Cadastro Auxiliar</span>
          </div>
          <h2 className="text-2xl font-serif font-bold text-white">Clientes & Fornecedores</h2>
          <p className="text-xs text-slate-400 mt-1">
            Cadastro dos parceiros que aparecem nos recibos e nos relatórios (aluguel, fornecedor de vinho, gráfica etc).
          </p>
        </div>
        <button
          data-testid="contacts-btn-new"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="flex items-center space-x-1.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
        >
          <Plus className="w-4 h-4" />
          <span>Novo Contato</span>
        </button>
      </div>

      <div className="luxury-card p-4 flex flex-col md:flex-row items-stretch md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="contacts-input-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, documento ou e-mail…"
            className="w-full bg-[#080C14] text-xs text-white placeholder-slate-500 pl-9 pr-3 py-2.5 rounded-xl border border-slate-700 focus:border-[#D4AF37]"
          />
        </div>
        <select
          data-testid="contacts-select-kind"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="bg-[#080C14] text-xs text-white px-3 py-2.5 rounded-xl border border-slate-700 focus:border-[#D4AF37]"
        >
          <option value="all">Todos os tipos</option>
          <option value="cliente">Clientes</option>
          <option value="fornecedor">Fornecedores</option>
          <option value="terceiro">Terceiros</option>
        </select>
      </div>

      <div className="luxury-card overflow-hidden">
        <table className="w-full text-left text-sm" data-testid="contacts-table">
          <thead>
            <tr className="text-xs text-slate-400 uppercase bg-[#080C14]/80 border-b border-slate-800">
              <th className="py-3.5 px-4">Nome</th>
              <th className="py-3.5 px-4">Tipo</th>
              <th className="py-3.5 px-4">Documento</th>
              <th className="py-3.5 px-4">E-mail</th>
              <th className="py-3.5 px-4">Telefone</th>
              <th className="py-3.5 px-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {filtered.map((c) => {
              const meta = KIND_META[c.kind] || KIND_META.terceiro;
              const Icon = meta.icon;
              return (
                <tr key={c.id} data-testid={`contact-row-${c.id}`} className="hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4 text-white font-semibold">{c.name}</td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${meta.tone}`}>
                      <Icon className="w-3 h-3" />
                      {meta.label}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-300 font-mono text-xs">{c.document || "—"}</td>
                  <td className="py-3 px-4 text-slate-300 text-xs">{c.email || "—"}</td>
                  <td className="py-3 px-4 text-slate-300 text-xs font-mono">{c.phone || "—"}</td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => {
                        setEditing(c);
                        setModalOpen(true);
                      }}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 mr-1"
                      title="Editar"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"
                      title="Excluir"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-xs text-slate-500 italic">
                  Nenhum contato cadastrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ContactModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSave}
        initialData={editing}
      />
    </div>
  );
};

const ContactModal = ({ isOpen, onClose, onSubmit, initialData }) => {
  const [form, setForm] = useState({
    name: "",
    kind: "fornecedor",
    document: "",
    email: "",
    phone: "",
    notes: "",
  });

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: initialData?.name || "",
        kind: initialData?.kind || "fornecedor",
        document: initialData?.document || "",
        email: initialData?.email || "",
        phone: initialData?.phone || "",
        notes: initialData?.notes || "",
      });
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Informe o nome.");
      return;
    }
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="w-full max-w-lg bg-[#131C2E] border border-[#D4AF37]/40 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="font-serif font-bold text-lg text-white">
              {initialData?.id ? "Editar Contato" : "Novo Contato"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4 text-xs text-slate-300">
          <div>
            <label className="block mb-1 font-semibold">Nome *</label>
            <input
              data-testid="contact-input-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold">Tipo</label>
              <select
                data-testid="contact-select-kind"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-xs"
              >
                <option value="fornecedor">Fornecedor</option>
                <option value="cliente">Cliente</option>
                <option value="terceiro">Terceiro</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 font-semibold">CNPJ / CPF</label>
              <input
                data-testid="contact-input-doc"
                value={form.document}
                onChange={(e) => setForm({ ...form, document: e.target.value })}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-sm font-mono"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 font-semibold">E-mail</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-sm"
              />
            </div>
            <div>
              <label className="block mb-1 font-semibold">Telefone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-sm font-mono"
              />
            </div>
          </div>
          <div>
            <label className="block mb-1 font-semibold">Observações</label>
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-[#080C14] text-white p-2.5 rounded-xl border border-slate-700 text-sm"
            />
          </div>
          <div className="pt-3 border-t border-slate-800 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 font-semibold text-xs"
            >
              Cancelar
            </button>
            <button
              data-testid="contact-btn-submit"
              type="submit"
              className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-6 py-2 rounded-xl text-xs uppercase"
            >
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
